import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { shopShelves } from '@kraftwerk/shared';
import { db } from '../../db/local.js';
import type { LocalHouseholdEvent, LocalShop } from '../../db/local.js';
import {
  addShop,
  deleteShop,
  listTrips,
  moveShopShelf,
  planTrip,
  removeTrip,
  renameShop,
} from '../../db/shops.js';
import { formatDay } from '../../format.js';
import { useToday } from '../../hooks/useToday.js';

/**
 * Läden wählen (18.10.2026): „Alle“ oder ein Laden — die Liste zeigt dann
 * dessen Artikel und alles, was egal ist, in seiner Regalfolge.
 */
export function ShopChips({
  shops,
  selected,
  counts,
  onSelect,
}: {
  shops: LocalShop[];
  selected: string | null;
  counts: Map<string | null, number>;
  onSelect: (shopId: string | null) => void;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  if (shops.length === 0) return null;
  const options = [{ id: null, name: t('shops.all') }, ...shops];
  return (
    <div className="flex flex-wrap gap-1" role="group" aria-label={t('shops.filter')}>
      {options.map((shop) => (
        <button
          key={shop.id ?? 'all'}
          type="button"
          aria-pressed={selected === shop.id}
          onClick={() => onSelect(shop.id)}
          className="kw-toggle"
        >
          {shop.name} · {counts.get(shop.id) ?? 0}
        </button>
      ))}
    </div>
  );
}

/** Läden anlegen, umbenennen, löschen und ihre Regalfolge ordnen. */
export function ShopManager({
  householdId,
  shops,
}: {
  householdId: string | null;
  shops: LocalShop[];
}): React.JSX.Element {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const id = useId();
  return (
    <details className="kw-tile">
      <summary className="kw-kicker cursor-pointer">{t('shops.manage')}</summary>
      <div className="mt-3 flex flex-col gap-3">
        <p className="text-sm text-ink-muted">{t('shops.intro')}</p>
        {shops.map((shop) => (
          <ShopRow key={shop.id} shop={shop} />
        ))}
        <form
          className="flex items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (!householdId || name.trim() === '') return;
            // Sofort leeren, nicht nach dem Speichern — sonst hängt ein schnell
            // getippter zweiter Name am ersten („Lidldm“, im Browser gefunden).
            const value = name;
            setName('');
            void addShop(householdId, value, db);
          }}
        >
          <label htmlFor={id} className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="kw-label">{t('shops.name')}</span>
            <input
              id={id}
              value={name}
              maxLength={60}
              onChange={(event) => setName(event.target.value)}
              className="kw-field"
            />
          </label>
          <button type="submit" className="kw-btn kw-btn-small shrink-0 px-4">
            {t('shops.add')}
          </button>
        </form>
      </div>
    </details>
  );
}

function ShopRow({ shop }: { shop: LocalShop }): React.JSX.Element {
  const { t } = useTranslation();
  const [name, setName] = useState(shop.name);
  const [ordering, setOrdering] = useState(false);
  const id = useId();
  const shelves = shopShelves(shop.shelfOrder);
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-line px-3 py-2">
      <div className="flex items-end gap-2">
        <label htmlFor={id} className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="sr-only">{t('shops.name')}</span>
          <input
            id={id}
            value={name}
            maxLength={60}
            onChange={(event) => setName(event.target.value)}
            onBlur={() => {
              if (name.trim() && name.trim() !== shop.name) void renameShop(shop.id, name, db);
              else setName(shop.name);
            }}
            className="kw-field"
          />
        </label>
        <button
          type="button"
          aria-expanded={ordering}
          onClick={() => setOrdering((value) => !value)}
          className="kw-btn kw-btn-quiet kw-btn-small shrink-0 px-3"
        >
          {t('shops.shelves')}
        </button>
        <button
          type="button"
          onClick={() => void deleteShop(shop.id, db)}
          aria-label={t('shops.remove', { name: shop.name })}
          className="kw-btn kw-btn-quiet kw-btn-small shrink-0 px-3 hover:border-danger hover:text-danger"
        >
          ×
        </button>
      </div>
      {ordering ? (
        <>
          <p className="text-xs text-ink-muted">{t('shops.shelvesIntro')}</p>
          <ol className="flex flex-col gap-1">
            {shelves.map((shelf, index) => (
              <li key={shelf} className="flex items-center justify-between gap-2 text-sm">
                <span>{t(`shopping.category.${shelf}`)}</span>
                <span className="flex gap-1">
                  <button
                    type="button"
                    disabled={index === 0}
                    onClick={() => void moveShopShelf(shop.id, shelf, -1, db)}
                    aria-label={t('shops.shelfUp', { name: t(`shopping.category.${shelf}`) })}
                    className="kw-btn kw-btn-quiet kw-btn-small px-3"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    disabled={index === shelves.length - 1}
                    onClick={() => void moveShopShelf(shop.id, shelf, 1, db)}
                    aria-label={t('shops.shelfDown', { name: t(`shopping.category.${shelf}`) })}
                    className="kw-btn kw-btn-quiet kw-btn-small px-3"
                  >
                    ↓
                  </button>
                </span>
              </li>
            ))}
          </ol>
        </>
      ) : null}
    </div>
  );
}

/**
 * Einkaufstage (18.10.2026): einzeln geplant, wann man eben geht — mit Laden
 * oder ohne. Sie stehen als Haushaltstermin im Kalender, „Heute“ zeigt am Tag
 * die passende Liste, und der Wochenplan rechnet bis zum nächsten.
 */
export function TripPlanner({
  householdId,
  shops,
}: {
  householdId: string | null;
  shops: LocalShop[];
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const today = useToday();
  const trips = useLiveQuery(
    () => (today ? listTrips(householdId, today, db) : Promise.resolve([])),
    [householdId, today],
    [] as LocalHouseholdEvent[],
  );
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [shopId, setShopId] = useState('');
  const ids = { date: useId(), time: useId(), shop: useId() };
  const nameOf = new Map(shops.map((shop) => [shop.id, shop.name]));

  return (
    <section aria-labelledby="trips-heading" className="kw-tile flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <h3 id="trips-heading" className="kw-kicker">
          {t('shops.trips')}
        </h3>
        <button
          type="button"
          aria-expanded={open}
          onClick={() => {
            setDate((value) => value || today || '');
            setOpen((value) => !value);
          }}
          className="kw-btn kw-btn-quiet kw-btn-small shrink-0 px-3"
        >
          {t('shops.planTrip')}
        </button>
      </div>
      {trips.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('shops.noTrips')}</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {trips.map((trip) => (
            <li key={trip.id} className="flex items-center justify-between gap-2 text-sm">
              <span className="min-w-0">
                <span className="font-semibold">
                  {formatDay(trip.date, 'short', i18n.language)}
                </span>
                {trip.time ? ` · ${trip.time}` : ''} ·{' '}
                {trip.shopId
                  ? (nameOf.get(trip.shopId) ?? t('shops.anywhere'))
                  : t('shops.anywhere')}
              </span>
              <button
                type="button"
                onClick={() => void removeTrip(trip.id, db)}
                aria-label={t('shops.removeTrip', {
                  day: formatDay(trip.date, 'short', i18n.language),
                })}
                className="kw-btn kw-btn-quiet kw-btn-small shrink-0 px-3"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      {open ? (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (!householdId || date === '') return;
            const shop = shopId ? (nameOf.get(shopId) ?? null) : null;
            void planTrip(
              {
                householdId,
                date,
                time: time || null,
                shopId: shopId || null,
                title: shop ? t('shops.tripTitleShop', { shop }) : t('shops.tripTitle'),
              },
              db,
            ).then(() => setOpen(false));
          }}
        >
          <label htmlFor={ids.date} className="flex flex-col gap-1">
            <span className="kw-label">{t('schedule.date')}</span>
            <input
              id={ids.date}
              type="date"
              value={date}
              min={today ?? undefined}
              onChange={(event) => setDate(event.target.value)}
              className="kw-field"
            />
          </label>
          <label htmlFor={ids.time} className="flex flex-col gap-1">
            <span className="kw-label">{t('shops.timeOptional')}</span>
            <input
              id={ids.time}
              type="time"
              value={time}
              onChange={(event) => setTime(event.target.value)}
              className="kw-field"
            />
          </label>
          {shops.length > 0 ? (
            <label htmlFor={ids.shop} className="flex min-w-32 flex-1 flex-col gap-1">
              <span className="kw-label">{t('shops.shop')}</span>
              <select
                id={ids.shop}
                value={shopId}
                onChange={(event) => setShopId(event.target.value)}
                className="kw-field"
              >
                <option value="">{t('shops.anywhere')}</option>
                {shops.map((shop) => (
                  <option key={shop.id} value={shop.id}>
                    {shop.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <button type="submit" className="kw-btn kw-btn-small px-4">
            {t('shops.save')}
          </button>
        </form>
      ) : null}
    </section>
  );
}
